import { apiRequest } from "./apiClient";
import type {
  AiSuggestionCountsByRisk,
  Contract,
  ContractSourceKind,
  ContractStatus,
} from "../types/contract";
import { formatDisplayDate } from "../utils/displayDate";

export type GeneratedContractDraft = {
  readonly generatedContractId: string;
  readonly storedFileId: string;
  readonly sourceTemplateId: string;
  readonly documentWebUrl: string;
  /**
   * Legacy `ms-word:ofe|u|…` URI from API. Do not use for primary open flows — use Graph +
   * `insertFileFromBase64` via `graphService` / `hostDocumentService` instead.
   */
  readonly wordDesktopOpenUrl: string;
};

type BackendDraftResponse = {
  readonly success: boolean;
  readonly data: {
    readonly generated_contract_id: string;
    readonly stored_file_id: string;
    readonly source_template_id: string;
    readonly document_web_url: string;
    readonly word_desktop_open_url: string;
  };
};

export type GeneratedContractCommitResponseData = {
  readonly generated_contract_id: string;
  readonly stored_file_id: string;
  readonly source_template_id: string;
  readonly graph_drive_id?: string | null;
  readonly graph_item_id?: string | null;
};

type BackendCommitResponse = {
  readonly success: boolean;
  readonly message?: string;
  readonly data: GeneratedContractCommitResponseData;
};

type BackendStoredFileBase64Response = {
  readonly success: boolean;
  readonly data: {
    readonly base64: string;
  };
};

export type CommitPayload = {
  readonly display_name: string;
  readonly form_answers_json: unknown;
  readonly business_unit: string;
};

export type CreateDraftOptions = {
  /** Non-empty after trim; persisted as `display_name` on the draft row. */
  readonly displayName: string;
};

export const createDraft = async (
  sourceTemplateId: string,
  options: CreateDraftOptions,
): Promise<GeneratedContractDraft> => {
  const displayName = options.displayName.trim();
  if (!displayName) {
    throw new Error("Document name is required.");
  }

  const response = await apiRequest("/generated-contracts-v2/draft", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      source_template_id: sourceTemplateId,
      display_name: displayName,
    }),
  });

  const json = (await response.json()) as BackendDraftResponse;
  const d = json.data;

  return {
    generatedContractId: d.generated_contract_id,
    storedFileId: d.stored_file_id,
    sourceTemplateId: d.source_template_id,
    documentWebUrl: d.document_web_url,
    wordDesktopOpenUrl: d.word_desktop_open_url,
  };
};

export const getDraftFileBase64 = async (
  storedFileId: string,
): Promise<string> => {
  const response = await apiRequest(
    `/stored-files/${storedFileId}/content-base64`,
    {
      method: "GET",
    },
  );
  const json = (await response.json()) as BackendStoredFileBase64Response;
  const b64 = json.data?.base64;
  if (typeof b64 !== "string" || !b64.trim()) {
    throw new Error("Missing base64 in storage response");
  }
  return b64.trim();
};

export const commitGeneratedContract = async (
  generatedContractId: string,
  payload: CommitPayload,
): Promise<BackendCommitResponse> => {
  const response = await apiRequest(
    `/generated-contracts-v2/${generatedContractId}/commit`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );

  const json = (await response.json()) as BackendCommitResponse;
  return json;
};

export type AssignReviewPayload = {
  readonly reviewer_user_id: string;
  readonly review_note: string;
};

/** POST /generated-contracts/{id}/assign-review — not the v2 commit path (**FR-013**). */
export const assignGeneratedContractReview = async (
  generatedContractId: string,
  payload: AssignReviewPayload,
): Promise<void> => {
  const response = await apiRequest(
    `/generated-contracts/${generatedContractId}/assign-review`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        reviewer_user_id: payload.reviewer_user_id,
        review_note: payload.review_note,
      }),
    },
    { requiresAuth: true, redirectOnUnauthorized: true },
  );
  await response.json();
};

type BackendGeneratedContractRow = {
  readonly id: string;
  readonly display_name: string | null;
  readonly reviewer_user_id?: string | null;
  readonly source_template_id?: string | null;
  readonly status: string;
  readonly review_status: string | null;
  readonly contract_type: string | null;
  readonly source_kind: string;
  readonly created_at: string;
  readonly contract_origination: string | null;
  readonly library_processing_stage: string | null;
  readonly stored_file_id: string;
  readonly stored_file: {
    readonly file_name: string | null;
    readonly drive_id?: string | null;
    readonly item_id?: string | null;
  } | null;
  readonly ai_suggestion_counts_by_risk?: Readonly<Record<string, number>>;
};

type BackendPaginatedList = {
  readonly success: boolean;
  readonly data: readonly BackendGeneratedContractRow[];
  readonly meta?: {
    readonly total: number;
    readonly limit: number;
    readonly offset: number;
  };
};

type BackendSuccessDetail = {
  readonly success: boolean;
  readonly data: BackendGeneratedContractRow;
};

function mapApiStatus(row: BackendGeneratedContractRow): ContractStatus {
  const raw = (row.status ?? "").trim();
  const lower = raw.toLowerCase();
  if (lower === "completed" || lower === "review_completed") {
    return "Completed";
  }
  if (
    raw === "processing" ||
    raw === "processing_failed" ||
    raw === "ai_in_progress" ||
    raw === "generated" ||
    raw === "Pending Review" ||
    raw === "in_review" ||
    raw === "Assign to Reviewer" ||
    raw === "Completed"
  ) {
    return raw;
  }

  // Backwards compatibility for older status values.
  if (row.status === "pending_review") {
    return "Pending Review";
  }
  if (row.status === "approved") {
    return "Approved";
  }
  return "Draft";
}

function mapSourceKind(raw: string): ContractSourceKind | undefined {
  if (raw === "upload_review" || raw === "template_generated") {
    return raw;
  }
  return undefined;
}

function mapAiSuggestionCountsByRisk(
  raw: Readonly<Record<string, number>> | undefined,
): AiSuggestionCountsByRisk {
  return {
    ExecDecision: Number(raw?.ExecDecision ?? 0),
    BusinessDecision: Number(raw?.BusinessDecision ?? 0),
    LegalDecision: Number(raw?.LegalDecision ?? 0),
  };
}

/** Select Contract: template-generated + Draft + ids → navigate to `ReviewTemplatePage` / `ContractReviewDetailsView`. */
export function isEligibleTemplateDraftForContractReviewDetails(
  contract: Contract,
): boolean {
  if (contract.sourceKind !== "template_generated") {
    return false;
  }
  if (contract.status !== "Draft") {
    return false;
  }
  const tid = contract.sourceTemplateId?.trim() ?? "";
  const sid = contract.storedFileId?.trim() ?? "";
  return tid.length > 0 && sid.length > 0;
}

export type ReviewTemplatePageNavigationState = {
  readonly draft: GeneratedContractDraft;
  readonly capturedDocumentName: string;
};

/** Router state for `/contractiq/templates/:id/review`; list rows omit web URLs — use empty strings. */
export function buildReviewTemplatePageStateFromContract(
  contract: Contract,
): ReviewTemplatePageNavigationState | null {
  if (!isEligibleTemplateDraftForContractReviewDetails(contract)) {
    return null;
  }
  const sourceTemplateId = contract.sourceTemplateId!.trim();
  const storedFileId = contract.storedFileId!.trim();
  const draft: GeneratedContractDraft = {
    generatedContractId: contract.id,
    storedFileId,
    sourceTemplateId,
    documentWebUrl: "",
    wordDesktopOpenUrl: "",
  };
  const capturedDocumentName = contract.title.trim() || "Untitled";
  return { draft, capturedDocumentName };
}

function libraryStageShortLabel(
  stage: string | null | undefined,
): string | null {
  if (stage == null || stage === "") {
    return null;
  }
  switch (stage) {
    case "library_queued":
      return "Library: queued";
    case "library_processing":
      return "Library: preparing";
    case "library_completed":
      return "Library: ready";
    case "library_failed":
      return "Library: failed";
    case "library_skipped_unsupported_file":
      return "Library: skipped";
    default:
      return `Library: ${stage}`;
  }
}

export function mapGeneratedContractRowToContract(
  row: BackendGeneratedContractRow,
): Contract {
  const title = row.display_name?.trim() || "Untitled";
  const typeLabel = row.contract_type?.trim() || "—";
  const fileName = row.stored_file?.file_name?.trim() || null;
  const rawReviewerId = row.reviewer_user_id;
  const reviewerTrim =
    rawReviewerId != null && rawReviewerId !== ""
      ? String(rawReviewerId).trim()
      : "";
  const base: Contract = {
    id: row.id,
    title,
    type: typeLabel,
    date: formatDisplayDate(row.created_at),
    status: mapApiStatus(row),
    documentRef: null,
    fileName,
    origination: row.contract_origination,
    sourceKind: mapSourceKind(row.source_kind),
    libraryPrepLabel: libraryStageShortLabel(row.library_processing_stage),
    storedFileId: String(row.stored_file_id),
  };
  const rawTplId = row.source_template_id;
  if (rawTplId != null && rawTplId !== "") {
    const tplTrim = String(rawTplId).trim();
    if (tplTrim.length > 0) {
      base.sourceTemplateId = tplTrim;
    }
  }
  if (reviewerTrim.length > 0) {
    base.reviewerUserId = reviewerTrim;
  }
  const spDrive = row.stored_file?.drive_id?.trim();
  const spItem = row.stored_file?.item_id?.trim();
  if (spDrive && spItem) {
    base.graphDriveId = spDrive;
    base.graphItemId = spItem;
  }
  if (row.ai_suggestion_counts_by_risk != null) {
    base.aiSuggestionCountsByRisk = mapAiSuggestionCountsByRisk(
      row.ai_suggestion_counts_by_risk,
    );
  }
  return base;
}

export async function listMyGeneratedContracts(): Promise<Contract[]> {
  const response = await apiRequest(
    "/generated-contracts?limit=200&offset=0",
    { method: "GET" },
    { requiresAuth: true },
  );
  const json = (await response.json()) as BackendPaginatedList;
  const rows = json.data ?? [];
  return rows.map(mapGeneratedContractRowToContract);
}

/** GET /generated-contracts/{id} — for review route when not served from list cache. */
export async function getGeneratedContractById(
  id: string,
): Promise<Contract | null> {
  const response = await apiRequest(
    `/generated-contracts/${encodeURIComponent(id)}`,
    { method: "GET" },
    { requiresAuth: true },
  );
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error("Unable to load contract");
  }
  const json = (await response.json()) as BackendSuccessDetail;
  return mapGeneratedContractRowToContract(json.data);
}
